<template>
  <section
    class="subscription-box-production-batch-detail"
    v-if="!loading.productionBatch"
    v-loading="saving || printing">

    <!-- Header -->
    <el-header>
      <el-col :span="12">
        <el-page-header
          @back="back"
          :content="isNew ? 'Novo lote de montagem' : `Lote de montagem #${subscriptionBoxProductionBatch.id}`">
        </el-page-header>
      </el-col>

      <el-col :span="12" style="display: flex; gap: 8px; justify-content: flex-end;">
        <el-button v-if="!isNew" @click="showAddOrdersDialog">Adicionar pedidos</el-button>
        <el-button type="primary" @click="save">{{ isNew ? 'Criar lote' : 'Salvar' }}</el-button>

        <el-dropdown v-if="!isNew" trigger="click" @command="handleBatchCommand">
          <el-button icon="el-icon-more"></el-button>

          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="open-legacy">Abrir a página antiga</el-dropdown-item>
            <el-dropdown-item icon="el-icon-delete" command="delete" divided>
              Apagar lote e liberar as caixas
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </el-col>
    </el-header>

    <!-- Batch details. Collapsed on an existing batch, always open while creating one. -->
    <el-collapse class="batch-details" v-model="openDetailPanels">
      <el-collapse-item name="details" :disabled="isNew">
        <template slot="title">
          <div class="batch-details-title">
            <span class="batch-details-name">
              {{ isNew ? 'Detalhes do lote' : subscriptionBoxProductionBatch.name }}
            </span>
            <span class="batch-details-summary" v-if="!isNew">
              {{ batchTypeLabel }} &nbsp;|&nbsp;
              Edição {{ subscriptionBoxProductionBatch.edition }} &nbsp;|&nbsp;
              {{ orders.length }} caixas
            </span>
          </div>
        </template>

        <el-form ref="form" :model="subscriptionBoxProductionBatch" :rules="rules" label-width="160px">
          <el-form-item label="ID" prop="id" v-if="!isNew">
            <el-input v-model="subscriptionBoxProductionBatch.id" :disabled="true" class="stretched"></el-input>
          </el-form-item>

          <el-form-item label="Nome" prop="name">
            <el-input v-model="subscriptionBoxProductionBatch.name" placeholder="Nome" class="stretched"></el-input>
          </el-form-item>

          <el-form-item label="Tipo" prop="type">
            <el-select
              class="stretched"
              v-model="subscriptionBoxProductionBatch.type"
              placeholder="Selecione o tipo"
              :disabled="!isNew">
              <el-option key="STANDARD" label="Padrão" value="STANDARD"></el-option>
              <el-option key="MARKETING" label="Marketing" value="MARKETING"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Edição" prop="edition">
            <snowflake-remote-search-select
              v-model="subscriptionBoxProductionBatch.edition"
              placeholder="Edição"
              orderBy="id"
              sortDirection="DESC"
              :disabled="!isNew"
              :search-function="subscriptionBoxEditionService.search"
              :find-function="subscriptionBoxEditionService.find"
              :value-function="(item) => item.id">
            </snowflake-remote-search-select>
          </el-form-item>
        </el-form>
      </el-collapse-item>
    </el-collapse>

    <!-- Everything below needs a saved batch with orders in it. -->
    <template v-if="!isNew">

    <!-- Batch situation -->
    <div class="situation">
      <div class="situation-header">
        <span class="situation-hint">
          Situação das {{ orders.length }} caixas — clique em um número para filtrar a tabela
        </span>

        <div class="situation-metrics">
          <span v-if="averageProductCost !== null">CMV médio R${{ averageProductCost.toFixed(2) }}</span>
          <span v-if="averageTypeIntervalStrictness !== null">
            Intervalo médio {{ averageTypeIntervalStrictness.toFixed(2) }}
          </span>
          <span class="is-warning" v-if="numberOfUndesiredOrders > 0">
            {{ numberOfUndesiredOrders }} fora do perfil
          </span>
        </div>
      </div>

      <production-batch-counters
        :stages="stageCounters"
        :exceptions="exceptionCounters"
        :active-key="counterFilter"
        @select="onCounterSelect">
      </production-batch-counters>
    </div>

    <!-- Awaiting Bling / sync -->
    <el-alert
      v-if="awaitingSyncOrders.length > 0"
      class="sync-alert"
      type="warning"
      :closable="false"
      show-icon>
      <template slot="title">
        {{ awaitingSyncOrders.length }} caixas com NF gerada e ainda sem retorno do Bling
      </template>

      <div class="sync-alert-body">
        <span>
          Envie as NFs à SEFAZ no Bling e sincronize para trazer a NFe e o código de rastreamento.
          A transportadora escolhida na emissão ainda pode ser trocada no Bling até esse momento.
          Sem rastreio a smart label não sai.
        </span>

        <div class="sync-alert-actions">
          <el-button size="mini" @click="onCounterSelect('awaiting-sync')">
            Ver as {{ awaitingSyncOrders.length }} caixas
          </el-button>
          <el-button size="mini" type="warning" @click="showSyncOrderDialog(awaitingSyncOrders)">
            Sincronizar estas caixas
          </el-button>
        </div>
      </div>
    </el-alert>

    <!-- Printing -->
    <el-card class="printing-card" shadow="never">
      <div slot="header" class="printing-card-header">
        <div class="printing-card-title">
          <span class="printing-card-heading">Impressão</span>
        </div>

        <el-button
          size="small"
          :disabled="billOfMaterialsOrders.length === 0"
          @click="printBillOfMaterials">
          Lista de materiais — {{ billOfMaterialsOrders.length }} caixas
        </el-button>
      </div>

      <el-table :data="carrierRows" :show-header="carrierRows.length > 0" empty-text="Nenhuma NF gerada ainda — a transportadora é definida na emissão das notas fiscais">
        <el-table-column label="Transportadora" min-width="150">
          <template slot-scope="scope">
            <span :class="{ 'is-unassigned': !scope.row.company }">{{ scope.row.label }}</span>
          </template>
        </el-table-column>

        <el-table-column label="A imprimir" width="120">
          <template slot-scope="scope">
            <span class="carrier-count">{{ scope.row.readyForLabels }}</span>
            <span class="carrier-total">de {{ scope.row.total }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Fora da impressão">
          <template slot-scope="scope">
            <span class="carrier-pending" v-if="scope.row.canceled > 0">
              {{ scope.row.canceled }} {{ scope.row.canceled === 1 ? 'cancelada' : 'canceladas' }}
            </span>
            <span class="carrier-separator" v-if="scope.row.canceled > 0 && scope.row.awaitingSync > 0">·</span>
            <span class="carrier-warning" v-if="scope.row.awaitingSync > 0">
              <i class="el-icon-warning-outline"></i>
              {{ scope.row.awaitingSync }} sem rastreio — aguardando sync
            </span>
            <el-button
              type="text"
              size="mini"
              v-if="scope.row.awaitingSync > 0"
              @click="filterAwaitingSyncFor(scope.row.company)">
              Ver na tabela
            </el-button>
            <span class="carrier-pending" v-if="scope.row.canceled === 0 && scope.row.awaitingSync === 0">—</span>
          </template>
        </el-table-column>

        <el-table-column label="Imprimir só desta transportadora" width="300" align="right">
          <template slot-scope="scope">
            <el-button
              type="primary"
              size="small"
              :disabled="scope.row.readyForLabels === 0"
              @click="printSmartLabels(scope.row)">
              Smart labels
            </el-button>

            <el-button
              size="small"
              :disabled="scope.row.readyForLetters === 0"
              @click="printLetters(scope.row)">
              Cartas
            </el-button>

            <el-dropdown trigger="click" @command="command => handleCarrierCommand(command, scope.row)">
              <el-button size="small" icon="el-icon-more"></el-button>

              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="cargo-manifest" :disabled="scope.row.readyForLabels === 0">
                  Romaneio de coleta — {{ scope.row.readyForLabels }}
                </el-dropdown-item>
                <el-dropdown-item command="danfe" divided :disabled="scope.row.readyForDanfe === 0">
                  Notas fiscais (DANFE) — {{ scope.row.readyForDanfe }}
                </el-dropdown-item>
                <el-dropdown-item
                  command="shipping-labels"
                  v-if="scope.row.company !== 'SMARTENVIOS'"
                  :disabled="scope.row.readyForLabels === 0">
                  Etiquetas de transporte — {{ scope.row.readyForLabels }}
                </el-dropdown-item>
                <el-dropdown-item command="sync" divided>
                  Sincronizar as caixas desta transportadora
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Table toolbar -->
    <div class="orders-toolbar">
      <div class="orders-toolbar-filters">
        <el-input
          v-model="searchTerm"
          class="orders-search"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="Buscar ID caixa, ID cliente, NF ou rastreio">
        </el-input>

        <el-select v-model="statusFilter" size="small" clearable placeholder="Status" class="orders-filter">
          <el-option
            v-for="status in availableStatuses"
            :key="status"
            :label="orderStatus[status] || status"
            :value="status">
          </el-option>
        </el-select>

        <el-select
          v-model="carrierFilter"
          size="small"
          clearable
          placeholder="Transportadora"
          class="orders-filter">
          <el-option
            v-for="company in availableCarriers"
            :key="company"
            :label="shippingCompanyLabel(company)"
            :value="company">
          </el-option>
        </el-select>

        <el-button
          type="text"
          size="mini"
          class="orders-clear-filters"
          :class="{ 'is-hidden': !hasActiveFilters }"
          @click="clearFilters">
          Limpar filtros
        </el-button>
      </div>

      <div class="orders-toolbar-actions">
        <span class="orders-selection-count">
          {{ selectedOrders.length }} de {{ orders.length }} selecionadas
        </span>

        <el-button size="small" :disabled="selectedOrders.length === 0" @click="showSelectProductsDialog">
          Selecionar produtos
        </el-button>
        <el-button size="small" :disabled="selectedOrders.length === 0" @click="showGenerateNFeDialog">
          Gerar notas fiscais
        </el-button>
        <el-button size="small" :disabled="selectedOrders.length === 0" @click="showSyncOrderDialog()">
          Sincronizar
        </el-button>

        <el-dropdown trigger="click" @command="handleSelectionCommand">
          <el-button size="small" icon="el-icon-more"></el-button>

          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="print-letters" :disabled="lettersForSelection.length === 0">
              Imprimir cartas das selecionadas — {{ lettersForSelection.length }}
            </el-dropdown-item>
            <el-dropdown-item
              command="revert-product-selection"
              divided
              :disabled="selectedOrders.length === 0">
              Reverter seleção de produtos
            </el-dropdown-item>
            <el-dropdown-item command="remove-orders" :disabled="selectedOrders.length === 0" divided>
              Remover do lote
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <!-- Orders -->
    <el-table
      ref="ordersTable"
      max-height="520"
      row-key="id"
      :data="filteredOrders"
      :row-class-name="orderRowClassName"
      style="width: 100%"
      v-loading="loading.orders"
      @selection-change="onOrderSelectionChange">

      <el-table-column type="selection" width="44" :reserve-selection="true" fixed></el-table-column>

      <el-table-column prop="id" label="Caixa" sortable width="100" fixed>
        <template slot-scope="scope">
          <router-link :to="`/subscription-box-orders/${scope.row.id}`" target="_blank" class="link">
            {{ scope.row.id }}
          </router-link>
        </template>
      </el-table-column>

      <el-table-column prop="customerId" label="Cliente" sortable width="110">
        <template slot-scope="scope">
          <router-link :to="`/customers/${scope.row.customerId}`" target="_blank" class="link">
            {{ scope.row.customerId }}
          </router-link>
        </template>
      </el-table-column>

      <el-table-column prop="status" label="Status" min-width="200" sortable>
        <template slot-scope="scope">
          <el-tag size="small" :type="statusTagType(scope.row.status)" disable-transitions>
            {{ orderStatus[scope.row.status] || scope.row.status }}
          </el-tag>
          <el-tag
            size="small"
            type="warning"
            disable-transitions
            v-if="scope.row.hasUndesired"
            style="margin-left: 6px;">
            fora do perfil
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="shippingCompany" label="Transportadora" width="150">
        <template slot-scope="scope">
          {{ scope.row.shippingCompany ? shippingCompanyLabel(scope.row.shippingCompany) : '—' }}
        </template>
      </el-table-column>

      <el-table-column prop="nfe" label="NF" width="130" sortable>
        <template slot-scope="scope">{{ scope.row.nfe || '—' }}</template>
      </el-table-column>

      <el-table-column prop="trackingCode" label="Rastreamento" width="190" :show-overflow-tooltip="true">
        <template slot-scope="scope">
          <span v-if="scope.row.trackingCode">{{ scope.row.trackingCode }}</span>
          <span class="row-warning" v-else-if="scope.row.nfe">
            <i class="el-icon-warning-outline"></i> aguardando sync
          </span>
          <span v-else>—</span>
        </template>
      </el-table-column>

      <el-table-column prop="productSelection.cost" label="CMV" sortable width="110" align="right">
        <template slot-scope="scope">
          <span v-if="scope.row.productSelection">
            R${{ ((scope.row.productSelection.cost || 0) / 100.0).toFixed(2) }}
          </span>
          <span v-else>—</span>
        </template>
      </el-table-column>

      <el-table-column
        prop="productSelection.expandedProductTypeIntervalStrictness"
        label="Intervalo"
        sortable
        width="110"
        align="right">
        <template slot-scope="scope">
          {{ scope.row.productSelection && scope.row.productSelection.expandedProductTypeIntervalStrictness !== null
            ? scope.row.productSelection.expandedProductTypeIntervalStrictness
            : '—' }}
        </template>
      </el-table-column>

      <el-table-column width="60" align="center" fixed="right">
        <template slot-scope="scope">
          <el-dropdown trigger="click" @command="command => handleOrderCommand(command, scope.row)">
            <el-button size="mini" icon="el-icon-more"></el-button>

            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="print-smart-label" :disabled="!canPrintLabelsFor(scope.row)">
                Imprimir smart label desta caixa
              </el-dropdown-item>
              <el-dropdown-item command="print-letter" :disabled="!canPrintLetterFor(scope.row)">
                Imprimir carta desta caixa
              </el-dropdown-item>
              <el-dropdown-item command="sync" divided>Sincronizar esta caixa</el-dropdown-item>
              <el-dropdown-item command="remove" divided>Remover do lote</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>

    <div class="orders-footer">
      <span>Mostrando {{ filteredOrders.length }} de {{ orders.length }} caixas</span>
      <span v-if="canceledOrders.length > 0">
        As {{ canceledOrders.length }} canceladas aparecem na tabela e ficam fora de toda impressão
      </span>
    </div>
    </template>

    <!-- Dialogs -->
    <el-dialog
      class="text-left-aligned"
      title="Adicionar pedidos"
      :visible.sync="addOrdersDialogVisible"
      v-loading="loading.addOrders">

      <el-form
        ref="addOrdersForm"
        label-width="180px"
        label-position="top"
        :rules="addOrdersRules"
        :model="addOrders">

        <el-form-item label="Quantidade de pedidos a adicionar" prop="next">
          <el-input-number
            v-model="addOrders.next"
            controls-position="right"
            :precision="0"
            :step="1"
            :min="1"
            :max="1000"
            class="stretched">
          </el-input-number>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="addOrdersDialogVisible = false">Voltar</el-button>
        <el-button type="primary" @click="doAddOrders">Adicionar pedidos</el-button>
      </span>
    </el-dialog>

    <el-dialog class="text-left-aligned" title="Selecionar produtos" :visible.sync="selectProductsDialogVisible">
      <span v-if="!loading.selectProducts">
        Selecionar produtos para {{ selectProductsTargets.length }} caixas com status "Novo".
        <template v-if="selectedOrders.length !== selectProductsTargets.length">
          As demais {{ selectedOrders.length - selectProductsTargets.length }} caixas selecionadas serão ignoradas.
        </template>
      </span>

      <div class="text-center-aligned" v-else>
        <el-progress type="circle" :percentage="selectProductsProgress" :status="selectProductsStatus"></el-progress>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="selectProductsDialogVisible = false">Fechar</el-button>
        <el-button
          type="primary"
          :disabled="loading.selectProducts || selectProductsTargets.length === 0"
          @click="doSelectProducts">
          Selecionar produtos
        </el-button>
      </span>
    </el-dialog>

    <el-dialog class="text-left-aligned" title="Gerar notas fiscais" :visible.sync="generateNFeDialogVisible">
      <span v-if="!loading.generateNFe">
        Gerar notas fiscais para {{ generateNFeTargets.length }} caixas com produtos selecionados.
        <template v-if="selectedOrders.length !== generateNFeTargets.length">
          As demais {{ selectedOrders.length - generateNFeTargets.length }} caixas selecionadas serão ignoradas.
        </template>
      </span>

      <div class="text-center-aligned" v-else>
        <el-progress type="circle" :percentage="generateNFeProgress" :status="generateNFeStatus"></el-progress>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="generateNFeDialogVisible = false">Fechar</el-button>
        <el-button
          type="primary"
          :disabled="loading.generateNFe || generateNFeTargets.length === 0"
          @click="doGenerateNFe">
          Gerar notas fiscais
        </el-button>
      </span>
    </el-dialog>

    <el-dialog class="text-left-aligned" title="Sincronizar pedidos" :visible.sync="syncOrderDialogVisible">
      <span v-if="!loading.syncOrder">
        Sincronizar {{ syncTargets.length }} caixas com o Bling para trazer NFe e código de rastreamento.
      </span>

      <div class="text-center-aligned" v-else>
        <el-progress type="circle" :percentage="syncOrderProgress" :status="syncOrderStatus"></el-progress>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="syncOrderDialogVisible = false">Fechar</el-button>
        <el-button
          type="primary"
          :disabled="loading.syncOrder || syncTargets.length === 0"
          @click="doSyncOrder">
          Sincronizar pedidos
        </el-button>
      </span>
    </el-dialog>

    <iframe id="printing-frame" ref="printingFrame" :srcdoc="printingFrameSrcDoc"></iframe>
  </section>
</template>

<script>
import lodash from 'lodash';

import BillOfMaterialsService from '@/services/fulfillment/BillOfMaterialsService';
import CargoManifestService from '@/services/fulfillment/CargoManifestService';
import LetterService from '@/services/fulfillment/LetterService';
import NFeService from '@/services/fulfillment/NFeService';
import ProductSelectionService from '@/services/fulfillment/ProductSelectionService';
import ShippingLabelService from '@/services/fulfillment/ShippingLabelService';
import SmartEnviosLabelService from '@/services/fulfillment/SmartEnviosLabelService';
import SmartLabelService from '@/services/fulfillment/SmartLabelService';
import SubscriptionBoxOrderService from '@/services/fulfillment/SubscriptionBoxOrderService';
import SubscriptionBoxProductionBatchService from '@/services/fulfillment/SubscriptionBoxProductionBatchService';

import SubscriptionBoxEditionService from '@/services/fulfillment/SubscriptionBoxEditionService';

import ProductionBatchCounters from '@/components/fulfillment/ProductionBatchCounters.vue';
import SnowflakeRemoteSearchSelect from '@/components/input/RemoteSearchSelect.vue';

const orderStatus = SubscriptionBoxOrderService.statusTranslation;

// Display names for the shipping companies we know about. Any other value coming from the API is
// shown as-is, so a newly contracted carrier shows up without a code change here.
const shippingCompanyLabels = {
  CORREIOS: 'Correios',
  LOGGI: 'Loggi',
  MANDAE: 'Mandaê',
  TOTAL_EXPRESS: 'Total Express',
  CARRIERS: 'Carriers',
};

const statusTagTypes = {
  CANCELED: 'info',
  SCHEDULED: 'info',
  NEW: 'info',
  PRODUCT_SELECTION_FAILED: 'danger',
  SUSPECTED_FRAUD: 'danger',
  FRAUD: 'danger',
  DENIED: 'danger',
  PRODUCTS_SELECTED: '',
  IN_PRODUCTION: '',
  READY_TO_POST: 'success',
  IN_TRANSIT: 'success',
  AWAITING_CUSTOMER_PICKUP: 'warning',
  DELIVERED: 'success',
  RETURNED: 'warning',
  MISPLACED: 'danger',
};

// A box is only settled once the NFe was authorised by SEFAZ: the access key and the tracking code
// come back from Bling on sync, and from that point the shipping company can no longer change.
const isSynced = order => !!order.nfe && !!order.trackingCode && !!order.nfeAccessKey;
const isActive = order => order.status !== 'CANCELED';

export default {
  name: 'subscription-box-production-batch-detail',
  components: {
    ProductionBatchCounters,
    SnowflakeRemoteSearchSelect,
  },
  data() {
    return {
      orderStatus,

      rules: {
        name: [{ required: true, trigger: 'blur' }],
        type: [{ required: true, trigger: 'blur' }],
        edition: [{ required: true, trigger: 'blur' }],
      },

      openDetailPanels: [],
      subscriptionBoxEditionService: SubscriptionBoxEditionService,

      loading: {
        productionBatch: false,
        orders: false,
        addOrders: false,
        selectProducts: false,
        generateNFe: false,
        syncOrder: false,
      },

      saving: false,
      printing: false,

      subscriptionBoxProductionBatch: {},

      orders: [],
      orderSelection: [],

      counterFilter: null,
      statusFilter: null,
      carrierFilter: null,
      searchTerm: '',

      addOrders: { next: 50 },
      addOrdersDialogVisible: false,
      addOrdersRules: {
        next: [{ required: true, trigger: 'blur' }],
      },

      selectProductsDialogVisible: false,
      selectProductsProgress: 0,
      selectProductsStatus: null,

      generateNFeDialogVisible: false,
      generateNFeProgress: 0,
      generateNFeStatus: null,

      syncOrderDialogVisible: false,
      syncOrderProgress: 0,
      syncOrderStatus: null,
      syncTargets: [],

      printingFrameSrcDoc: '',
    };
  },
  async created() {
    window.addEventListener('resize', this.relayoutTable);

    await this.load();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.relayoutTable);
  },
  watch: {
    // Creating a batch swaps this same component from the "new" route to the detail route, so the
    // router reuses the instance and created() never runs again. Reload on the route change.
    $route() {
      this.load();
    },
    // The alert and the carrier card appear and change height as the data lands, which moves the
    // table without el-table noticing. Re-measure so the columns stay aligned with the header.
    filteredOrders() {
      this.relayoutTable();
    },
    carrierRows() {
      this.relayoutTable();
    },
  },
  computed: {
    isNew() {
      return this.$route.name === 'subscription-box-production-batch-new';
    },
    batchTypeLabel() {
      return this.subscriptionBoxProductionBatch.type === 'MARKETING' ? 'Marketing' : 'Padrão';
    },
    activeOrders() {
      return this.orders.filter(isActive);
    },
    canceledOrders() {
      return this.orders.filter(order => order.status === 'CANCELED');
    },
    awaitingSyncOrders() {
      return this.activeOrders.filter(order => !!order.nfe && !isSynced(order));
    },
    numberOfUndesiredOrders() {
      return this.orders.filter(order => order.hasUndesired).length;
    },
    printingTitle() {
      return `#${this.subscriptionBoxProductionBatch.id} - ${this.subscriptionBoxProductionBatch.name}`;
    },
    counterPredicates() {
      return {
        'new': order => order.status === 'NEW',
        'products-selected': order => order.status === 'PRODUCTS_SELECTED',
        'nfe-generated': order => isActive(order) && !!order.nfe,
        synced: order => isActive(order) && isSynced(order),
        'ready-to-post': order => order.status === 'READY_TO_POST',
        failed: order => order.status === 'PRODUCT_SELECTION_FAILED',
        canceled: order => order.status === 'CANCELED',
        'awaiting-sync': order => isActive(order) && !!order.nfe && !isSynced(order),
      };
    },
    stageCounters() {
      const count = key => this.orders.filter(this.counterPredicates[key]).length;

      return [
        { key: 'new', label: 'Novas', value: count('new'), type: 'primary' },
        { key: 'products-selected', label: 'Produtos selecionados', value: count('products-selected'), type: 'primary' },
        { key: 'nfe-generated', label: 'NF gerada', value: count('nfe-generated'), type: 'primary' },
        { key: 'synced', label: 'Sincronizadas', value: count('synced'), type: 'primary' },
        { key: 'ready-to-post', label: 'Pronto para postagem', value: count('ready-to-post'), type: 'success' },
      ].map(counter => (counter.value === 0 ? { ...counter, type: 'muted' } : counter));
    },
    exceptionCounters() {
      const count = key => this.orders.filter(this.counterPredicates[key]).length;

      return [
        { key: 'failed', label: 'Seleção falhou', value: count('failed'), type: count('failed') ? 'danger' : 'muted' },
        {
          key: 'canceled',
          label: 'Canceladas',
          value: count('canceled'),
          type: 'muted',
          hint: count('canceled') ? 'não imprimem' : null,
        },
      ];
    },
    // One row per shipping company actually present in this batch, so a new carrier needs no change here.
    carrierRows() {
      const withNFe = this.orders.filter(order => !!order.nfe);

      return lodash
        .chain(withNFe)
        .groupBy(order => order.shippingCompany || '')
        .map((companyOrders, company) => {
          const active = companyOrders.filter(isActive);

          return {
            company: company || null,
            label: company ? this.shippingCompanyLabel(company) : 'Sem transportadora',
            total: companyOrders.length,
            canceled: companyOrders.length - active.length,
            awaitingSync: active.filter(order => !isSynced(order)).length,
            readyForLabels: active.filter(isSynced).length,
            readyForLetters: active.filter(order => !!order.nfe).length,
            readyForDanfe: active.filter(order => !!order.nfeAccessKey).length,
          };
        })
        .sortBy(row => row.label)
        .value();
    },
    billOfMaterialsOrders() {
      return this.activeOrders.filter(order => this.hasProducts(order));
    },
    availableStatuses() {
      return lodash.uniq(this.orders.map(order => order.status)).sort();
    },
    availableCarriers() {
      return lodash
        .chain(this.orders)
        .map(order => order.shippingCompany)
        .filter(company => !!company)
        .uniq()
        .sort()
        .value();
    },
    hasActiveFilters() {
      return !!this.counterFilter || !!this.statusFilter || !!this.carrierFilter || !!this.searchTerm;
    },
    filteredOrders() {
      const term = this.searchTerm.trim().toLowerCase();
      const counterPredicate = this.counterFilter ? this.counterPredicates[this.counterFilter] : null;

      return this.orders.filter((order) => {
        if (counterPredicate && !counterPredicate(order)) return false;
        if (this.statusFilter && order.status !== this.statusFilter) return false;
        if (this.carrierFilter && order.shippingCompany !== this.carrierFilter) return false;

        if (term) {
          const haystack = [order.id, order.customerId, order.nfe, order.trackingCode]
            .filter(value => value !== null && value !== undefined)
            .join(' ')
            .toLowerCase();

          if (!haystack.includes(term)) return false;
        }

        return true;
      });
    },
    // The table keeps the selection across reloads, so resolve it against the freshly loaded orders
    // instead of trusting the rows captured when the checkbox was ticked.
    selectedOrders() {
      const selectedIds = new Set(this.orderSelection.map(order => order.id));

      return this.orders.filter(order => selectedIds.has(order.id));
    },
    lettersForSelection() {
      return this.selectedOrders.filter(order => this.canPrintLetterFor(order));
    },
    selectProductsTargets() {
      return this.selectedOrders.filter(order => order.status === 'NEW');
    },
    generateNFeTargets() {
      return this.selectedOrders.filter(order => order.status === 'PRODUCTS_SELECTED');
    },
    averageTypeIntervalStrictness() {
      const withSelection = this.orders.filter(order => order.productSelection
        && order.productSelection.products.length > 0
        && order.productSelection.expandedProductTypeIntervalStrictness !== null
        && order.productSelection.expandedProductTypeIntervalStrictness !== undefined);

      if (withSelection.length === 0) return null;

      const total = withSelection
        .reduce((sum, order) => sum + order.productSelection.expandedProductTypeIntervalStrictness, 0);

      return total / withSelection.length;
    },
    averageProductCost() {
      const withSelection =
        this.orders.filter(order => order.productSelection && order.productSelection.products.length > 0);

      if (withSelection.length === 0) return null;

      const total = withSelection.reduce((sum, order) => sum + order.productSelection.cost, 0.0);

      return total / (withSelection.length * 100);
    },
  },
  methods: {
    async load() {
      if (this.isNew) {
        this.subscriptionBoxProductionBatch = { type: this.$route.params.type };
        this.openDetailPanels = ['details'];

        return;
      }

      this.loading.productionBatch = true;

      try {
        this.subscriptionBoxProductionBatch =
          await SubscriptionBoxProductionBatchService.get(this.$route.params.id);

        this.openDetailPanels = [];

        await this.loadOrders();
      } finally {
        this.loading.productionBatch = false;
      }
    },
    relayoutTable() {
      this.$nextTick(() => {
        if (this.$refs.ordersTable) this.$refs.ordersTable.doLayout();
      });
    },
    back() {
      // Opened straight from a pasted URL there is nothing to go back to, so fall back to the list
      // instead of leaving the button dead.
      if (window.history.length > 1) {
        this.$router.go(-1);

        return;
      }

      this.$router.push({ name: 'subscription-box-production-batch-list' });
    },
    shippingCompanyLabel(company) {
      return shippingCompanyLabels[company] || company;
    },
    statusTagType(status) {
      return statusTagTypes[status] !== undefined ? statusTagTypes[status] : '';
    },
    orderRowClassName({ row }) {
      return row.status === 'CANCELED' ? 'is-canceled' : '';
    },
    hasProducts(order) {
      return !!order.productSelection
        && (order.productSelection.products.length > 0 || order.additionalProducts.length > 0);
    },
    // SmartEnvios hands out a temporary tracking code when the NF is generated, so a tracking code
    // alone does not mean the box was synced. Labels need the NFe access key as well.
    canPrintLabelsFor(order) {
      return isActive(order) && isSynced(order);
    },
    canPrintLetterFor(order) {
      return isActive(order) && !!order.nfe;
    },
    async save() {
      // el-form's validate() rejects on invalid input instead of resolving to false.
      try {
        await this.$refs.form.validate();
      } catch (e) {
        this.$message.warning('Confira os detalhes do lote antes de salvar.');
        return;
      }

      this.saving = true;

      try {
        if (this.isNew) {
          this.subscriptionBoxProductionBatch =
            await SubscriptionBoxProductionBatchService.create(this.subscriptionBoxProductionBatch);

          this.$message.success('Lote criado com sucesso.');

          // replace, not push: the creation form must not stay in the history, otherwise "Voltar"
          // lands back on it and invites creating the same batch twice.
          this.$router.replace({
            name: 'subscription-box-production-batch-detail',
            params: { id: this.subscriptionBoxProductionBatch.id },
          });

          return;
        }

        this.subscriptionBoxProductionBatch = await SubscriptionBoxProductionBatchService.update(
          this.subscriptionBoxProductionBatch.id,
          this.subscriptionBoxProductionBatch,
        );

        this.$message.success('Lote salvo com sucesso.');
      } finally {
        this.saving = false;
      }
    },
    delete() {
      this.$confirm(
        `Apagar este lote devolve as ${this.orders.length} caixas para a fila, disponíveis para outros lotes.`,
        'Apagar lote e liberar as caixas?',
        { confirmButtonText: 'Apagar lote', cancelButtonText: 'Cancelar', type: 'warning' },
      ).then(() => SubscriptionBoxProductionBatchService.delete(this.subscriptionBoxProductionBatch.id)
        .then(() => {
          this.$message.success('Lote apagado, caixas liberadas.');
          this.$router.replace({ name: 'subscription-box-production-batch-list' });
        }));
    },
    async loadOrders() {
      this.loading.orders = true;

      try {
        const orders =
          await SubscriptionBoxProductionBatchService.getOrders(this.subscriptionBoxProductionBatch.id);

        this.orders = orders.map(order => ({
          ...order,
          hasUndesired: this.orderHasUndesired(order),
        }));
      } finally {
        this.loading.orders = false;
      }
    },
    orderHasUndesired(order) {
      if (!order.productSelection || order.productSelection.products.length <= 0) return false;

      return order.productSelection.products
        .some(product => order.productSelection.undesiredProducts.includes(product));
    },
    onOrderSelectionChange(selection) {
      this.orderSelection = selection;
    },
    onCounterSelect(key) {
      this.counterFilter = key;
      this.statusFilter = null;
    },
    filterAwaitingSyncFor(company) {
      this.counterFilter = 'awaiting-sync';
      this.carrierFilter = company;
    },
    clearFilters() {
      this.counterFilter = null;
      this.statusFilter = null;
      this.carrierFilter = null;
      this.searchTerm = '';
    },
    // Commands
    handleBatchCommand(command) {
      if (command === 'delete') this.delete();
      if (command === 'open-legacy') {
        this.$router.push({
          name: 'subscription-box-production-batch-detail-legacy',
          params: { id: this.subscriptionBoxProductionBatch.id },
        });
      }
    },
    handleSelectionCommand(command) {
      if (command === 'print-letters') this.printLettersForSelection();
      if (command === 'revert-product-selection') this.revertProductSelection();
      if (command === 'remove-orders') this.removeOrders();
    },
    printLettersForSelection() {
      const targets = this.lettersForSelection;

      this.reportSkipped(targets.length, this.selectedOrders.length, 'canceladas ou sem NF');

      return this.printLettersForOrders(targets);
    },
    handleCarrierCommand(command, row) {
      if (command === 'cargo-manifest') this.printCargoManifest(row);
      if (command === 'danfe') this.printDANFes(row);
      if (command === 'shipping-labels') this.printShippingLabels(row);
      if (command === 'sync') {
        this.showSyncOrderDialog(this.ordersForCompany(row.company).filter(isActive));
      }
    },
    handleOrderCommand(command, order) {
      if (command === 'print-smart-label') this.printSmartLabelsForOrders([order]);
      if (command === 'print-letter') this.printLettersForOrders([order]);
      if (command === 'sync') this.showSyncOrderDialog([order]);
      if (command === 'remove') this.removeOrder(order);
    },
    ordersForCompany(company) {
      return this.orders.filter(order => (order.shippingCompany || null) === company && !!order.nfe);
    },
    // Add / remove orders
    showAddOrdersDialog() {
      this.addOrders = { next: 50 };
      this.addOrdersDialogVisible = true;
    },
    async doAddOrders() {
      try {
        await this.$refs.addOrdersForm.validate();
      } catch (e) {
        return;
      }

      this.loading.addOrders = true;

      try {
        const previousCount = this.subscriptionBoxProductionBatch.orders.length;

        this.subscriptionBoxProductionBatch = await SubscriptionBoxProductionBatchService.addOrders(
          this.subscriptionBoxProductionBatch.id,
          { next: this.addOrders.next },
        );

        const added = this.subscriptionBoxProductionBatch.orders.length - previousCount;

        if (added < this.addOrders.next) {
          this.$message.warning(
            `${added} pedidos adicionados — não havia mais pedidos disponíveis para esta edição.`,
          );
        } else {
          this.$message.success(`${added} pedidos adicionados com sucesso!`);
        }
      } catch (e) {
        this.$message.error(`Falha ao adicionar pedidos: ${e.message}`);
      } finally {
        this.loading.addOrders = false;
        this.addOrdersDialogVisible = false;
        await this.loadOrders();
      }
    },
    removeOrder(order) {
      this.$confirm(
        `Remover a caixa ${order.id} deste lote?`,
        'Remover caixa',
        { confirmButtonText: 'Remover', cancelButtonText: 'Cancelar', type: 'warning' },
      ).then(async () => {
        this.saving = true;

        try {
          this.subscriptionBoxProductionBatch = await SubscriptionBoxProductionBatchService.removeOrder(
            this.subscriptionBoxProductionBatch.id,
            { orderId: order.id },
          );

          await this.loadOrders();

          this.$message.success('Caixa removida.');
        } finally {
          this.saving = false;
        }
      }).catch(() => {});
    },
    removeOrders() {
      this.$confirm(
        `Remover  caixas deste lote?`,
        'Remover caixas selecionadas',
        { confirmButtonText: 'Remover', cancelButtonText: 'Cancelar', type: 'warning' },
      ).then(async () => {
        const orderIds = this.selectedOrders.map(order => order.id);

        try {
          await SubscriptionBoxProductionBatchService.removeOrders(
            this.subscriptionBoxProductionBatch.id,
            { orderIds },
          );

          this.$refs.ordersTable.clearSelection();

          await this.loadOrders();

          this.$message.success('Caixas removidas.');
        } catch (e) {
          this.$message.error('Erro ao remover caixas.');
        }
      }).catch(() => {});
    },
    // Product selection
    showSelectProductsDialog() {
      this.selectProductsProgress = 0;
      this.selectProductsStatus = null;
      this.loading.selectProducts = false;
      this.selectProductsDialogVisible = true;
    },
    async doSelectProducts() {
      const targets = this.selectProductsTargets;

      this.selectProductsProgress = 0;
      this.loading.selectProducts = true;

      const chunks = lodash.chunk(targets, 5);
      let processed = 0;

      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const chunk of chunks) {
          // eslint-disable-next-line no-await-in-loop
          await ProductSelectionService.selectProducts(chunk.map(order => order.id), null, false);

          processed += chunk.length;
          this.selectProductsProgress = Math.round((processed / targets.length) * 100);
        }

        this.selectProductsProgress = 100;
        this.selectProductsStatus = 'success';
      } catch (e) {
        this.selectProductsStatus = 'exception';
        this.$message.error(`Falha ao selecionar produtos: ${e.message}`);
      } finally {
        await this.loadOrders();
      }
    },
    revertProductSelection() {
      const targets = this.selectedOrders
        .filter(order => order.status === 'PRODUCTS_SELECTED' || order.status === 'PRODUCT_SELECTION_FAILED');

      if (targets.length === 0) {
        this.$message.warning('Nenhuma das caixas selecionadas tem seleção de produtos para reverter.');
        return;
      }

      this.$confirm(
        `Reverter a seleção de produtos de ${targets.length} caixas? Esta ação é irreversível.`,
        'Reverter seleção de produtos',
        { confirmButtonText: 'Reverter', cancelButtonText: 'Cancelar', type: 'warning' },
      ).then(async () => {
        this.loading.orders = true;

        try {
          const chunks = lodash.chunk(targets, 5);

          // eslint-disable-next-line no-restricted-syntax
          for (const chunk of chunks) {
            // eslint-disable-next-line no-await-in-loop
            await ProductSelectionService.revertProductSelection(chunk.map(order => order.id));
          }

          this.$message.success('Seleção de produtos revertida.');
        } catch (e) {
          this.$message.error(`Falha ao reverter seleção de produtos: ${e.message}`);
        } finally {
          await this.loadOrders();
        }
      }).catch(() => {});
    },
    // NFe
    showGenerateNFeDialog() {
      this.generateNFeProgress = 0;
      this.generateNFeStatus = null;
      this.loading.generateNFe = false;
      this.generateNFeDialogVisible = true;
    },
    async doGenerateNFe() {
      const targets = this.generateNFeTargets;

      this.generateNFeProgress = 0;
      this.loading.generateNFe = true;

      const chunks = lodash.chunk(targets, 10);
      let processed = 0;

      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const chunk of chunks) {
          // eslint-disable-next-line no-await-in-loop
          const results = await NFeService.generateNFe(chunk.map(order => order.id), null);
          const failures = lodash.filter(results, result => !result.successful);

          if (failures.length > 0) {
            this.generateNFeStatus = 'exception';
            this.$message.error(`Falha ao gerar notas fiscais: ${failures[0].failureCause}`);

            return;
          }

          processed += chunk.length;
          this.generateNFeProgress = Math.round((processed / targets.length) * 100);
        }

        this.generateNFeProgress = 100;
        this.generateNFeStatus = 'success';
      } catch (e) {
        this.generateNFeStatus = 'exception';
        this.$message.error(`Falha ao gerar notas fiscais: ${e.message}`);
      } finally {
        await this.loadOrders();
      }
    },
    // Sync
    showSyncOrderDialog(targets) {
      this.syncTargets = (targets || this.selectedOrders).filter(order => order.status !== 'NEW');
      this.syncOrderProgress = 0;
      this.syncOrderStatus = null;
      this.loading.syncOrder = false;
      this.syncOrderDialogVisible = true;
    },
    async doSyncOrder() {
      const targets = this.syncTargets;

      this.syncOrderProgress = 0;
      this.loading.syncOrder = true;

      const chunks = lodash.chunk(targets, 10);
      let processed = 0;

      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const chunk of chunks) {
          // eslint-disable-next-line no-await-in-loop
          await SubscriptionBoxOrderService.sync(chunk.map(order => order.id));

          processed += chunk.length;
          this.syncOrderProgress = Math.round((processed / targets.length) * 100);
        }

        this.syncOrderProgress = 100;
        this.syncOrderStatus = 'success';
      } catch (e) {
        this.syncOrderStatus = 'exception';
        this.$message.error(`Falha ao sincronizar pedidos: ${e.message}`);
      } finally {
        await this.loadOrders();
      }
    },
    // Printing. Every print is scoped to one shipping company and skips cancelled boxes; what is not
    // ready is reported instead of blocking the whole job.
    reportSkipped(printed, total, reason) {
      if (printed === total) return;

      this.$message.warning(`${printed} de ${total} caixas impressas — ${total - printed} ${reason}.`);
    },
    async printBillOfMaterials() {
      const targets = this.billOfMaterialsOrders;

      if (targets.length === 0) {
        this.$message.warning('Nenhuma caixa com produtos selecionados para gerar a lista de materiais.');
        return;
      }

      this.reportSkipped(targets.length, this.activeOrders.length, 'ainda sem produtos selecionados');

      await this.print(
        () => BillOfMaterialsService.generateBillOfMaterials(this.printingTitle, targets.map(order => order.id)),
        'lista de materiais',
      );
    },
    printSmartLabels(row) {
      const targets = this.ordersForCompany(row.company).filter(order => this.canPrintLabelsFor(order));

      this.reportSkipped(targets.length, row.total, 'canceladas ou ainda não sincronizadas');

      return this.printSmartLabelsForOrders(targets);
    },
    async printSmartLabelsForOrders(targets) {
      if (targets.length === 0) {
        this.$message.warning('Nenhuma caixa pronta para imprimir smart labels.');
        return;
      }

      // SmartEnvios has no HTML smart label: the carrier issues it (SmartLabel + simplified DANFE)
      // as a PDF, composed 4-up server-side. Every print here is scoped to one shipping company.
      if (targets[0].shippingCompany === 'SMARTENVIOS') {
        await this.printSmartEnviosShippingLabels(targets);
        return;
      }

      await this.print(
        () => SmartLabelService.generateLabels(
          `#${this.subscriptionBoxProductionBatch.id}`,
          targets.map(order => order.id),
        ),
        'smart labels',
      );
    },
    async printShippingLabels(row) {
      const targets = this.ordersForCompany(row.company).filter(order => this.canPrintLabelsFor(order));

      if (targets.length === 0) {
        this.$message.warning('Nenhuma caixa pronta para imprimir etiquetas de transporte.');
        return;
      }

      this.reportSkipped(targets.length, row.total, 'canceladas ou ainda não sincronizadas');

      await this.print(
        () => ShippingLabelService.generateShippingLabels(
          `#${this.subscriptionBoxProductionBatch.id}`,
          targets.map(order => order.id),
        ),
        'etiquetas de transporte',
      );
    },
    async printSmartEnviosShippingLabels(targets) {
      this.printing = true;

      try {
        const { pdfBase64 } = await SmartEnviosLabelService.generateShippingLabels(
          `#${this.subscriptionBoxProductionBatch.id}`,
          targets.map(order => order.id),
        );

        if (!pdfBase64) {
          this.$message.error('Nenhuma etiqueta SmartEnvios foi gerada.');
          return;
        }

        this.printPdf(pdfBase64);
      } catch (e) {
        this.$message.error(`Falha ao imprimir etiquetas SmartEnvios: ${e.message}`);
      } finally {
        this.printing = false;
      }
    },
    printPdf(pdfBase64) {
      const bytes = Uint8Array.from(atob(pdfBase64), char => char.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Print silently through a hidden iframe (same UX as the HTML labels): the print dialog opens
      // directly, no extra tab. Falls back to a new tab if the viewer blocks programmatic printing.
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.right = '0';
      iframe.style.bottom = '0';

      const cleanup = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        URL.revokeObjectURL(url);
      };

      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          window.open(url, '_blank');
        }
        setTimeout(cleanup, 60000);
      };

      iframe.src = url;
      document.body.appendChild(iframe);
    },
    async printCargoManifest(row) {
      const targets = this.ordersForCompany(row.company).filter(order => this.canPrintLabelsFor(order));

      if (targets.length === 0) {
        this.$message.warning('Nenhuma caixa pronta para o romaneio. Sincronize os pedidos primeiro.');
        return;
      }

      this.reportSkipped(targets.length, row.total, 'canceladas ou ainda não sincronizadas');

      await this.print(
        () => CargoManifestService.generateCargoManifest(
          `#${this.subscriptionBoxProductionBatch.id}`,
          targets.map(order => order.id),
        ),
        'romaneio',
      );
    },
    async printDANFes(row) {
      const targets = this.ordersForCompany(row.company).filter(order => isActive(order) && !!order.nfeAccessKey);

      if (targets.length === 0) {
        this.$message.warning('Nenhuma caixa com chave de acesso da NFe. Sincronize os pedidos primeiro.');
        return;
      }

      this.reportSkipped(targets.length, row.total, 'canceladas ou sem chave de acesso da NFe');

      await this.print(
        () => NFeService.generateDANFe(
          `#${this.subscriptionBoxProductionBatch.id}`,
          targets.map(order => order.id),
        ),
        'notas fiscais',
      );
    },
    printLetters(row) {
      const targets = this.ordersForCompany(row.company).filter(order => this.canPrintLetterFor(order));

      this.reportSkipped(targets.length, row.total, 'canceladas ou sem NF');

      return this.printLettersForOrders(targets);
    },
    async printLettersForOrders(targets) {
      if (targets.length === 0) {
        this.$message.warning('Nenhuma caixa pronta para imprimir cartas.');
        return;
      }

      await this.print(() => LetterService.generateLetters(targets.map(order => order.id)), 'cartas');
    },
    async print(generate, label) {
      this.printingFrameSrcDoc = '';
      this.printing = true;

      try {
        const result = await generate();

        this.printingFrameSrcDoc = result.html;
      } catch (e) {
        this.$message.error(`Falha ao imprimir ${label}: ${e.message}`);
      } finally {
        this.printing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#printing-frame {
  position: absolute;
  top: -1000px;
  left: -1000px;
  width: 1px;
  height: 1px;
}

.subscription-box-production-batch-detail {
  // Nothing in the page may widen it past the content area. Deliberately no overflow-x here:
  // that would compute overflow-y to auto and nest a second scroll container inside the page.
  max-width: 100%;
  min-width: 0;

  .text-left-aligned { text-align: left; }
  .text-center-aligned { text-align: center; }

  .batch-details {
    margin-bottom: 4px;

    .batch-details-title {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      width: 100%;
      padding-right: 12px;
    }

    .batch-details-name {
      font-size: 14px;
      font-weight: 500;
      color: #303133;
    }

    .batch-details-summary {
      font-size: 13px;
      color: #909399;
    }
  }

  .situation {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px 0 22px;

    .situation-header {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 6px 16px;
    }

    .situation-hint {
      font-size: 13px;
      color: #909399;
    }

    .situation-metrics {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 6px 16px;
      font-size: 13px;
      color: #606266;

      .is-warning { color: #E6A23C; }
    }
  }

  .sync-alert {
    margin-bottom: 22px;

    .sync-alert-body {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 10px 16px;
    }

    .sync-alert-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  .printing-card {
    margin-bottom: 24px;

    .printing-card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 10px 16px;
    }

    .printing-card-title {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 4px 12px;
      flex: 1 1 320px;
      min-width: 0;
    }

    .printing-card-heading {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }

    .printing-card-hint {
      font-size: 13px;
      color: #909399;
    }

    .carrier-count {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }

    .carrier-total {
      font-size: 12px;
      color: #C0C4CC;
      margin-left: 6px;
    }

    .carrier-pending { color: #909399; }
    .carrier-separator { color: #DCDFE6; margin: 0 6px; }
    .carrier-warning { color: #E6A23C; }

    .is-unassigned { color: #E6A23C; }
  }

  .orders-toolbar {
    display: flex;
    // Top-aligned, not centred: "Limpar filtros" wraps onto a second row inside the filters group,
    // and centring would push the actions down to the middle of that now taller block.
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;

    .orders-toolbar-filters,
    .orders-toolbar-actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      min-width: 0;
    }

    .orders-toolbar-filters { flex: 1 1 380px; }
    .orders-toolbar-actions { flex: 0 1 auto; justify-content: flex-end; }

    // Filters and actions together need ~940px. The side menu eats a fixed slice of the viewport,
    // so below this width put them on their own rows instead of letting them squeeze the page.
    @media (max-width: 1500px) {
      .orders-toolbar-filters,
      .orders-toolbar-actions {
        flex: 1 1 100%;
      }

      .orders-toolbar-actions { justify-content: flex-start; }
    }

    .orders-search {
      flex: 1 1 200px;
      min-width: 170px;
      max-width: 300px;
      width: auto;
    }

    .orders-filter {
      flex: 0 1 160px;
      min-width: 130px;
      width: auto;
    }

    .orders-selection-count {
      font-size: 13px;
      color: #909399;
      margin-right: 4px;
      white-space: nowrap;
    }

    // Always occupies its slot so showing it never changes whether the filters wrap onto a second
    // row — otherwise typing the first character of a search bumps the whole table down.
    .orders-clear-filters {
      white-space: nowrap;

      &.is-hidden {
        visibility: hidden;
        pointer-events: none;
      }
    }
  }

  .orders-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 4px 16px;
    padding: 14px 0 24px;
    font-size: 13px;
    color: #909399;
  }

  .row-hint {
    margin-left: 8px;
    font-size: 12px;
    color: #C0C4CC;
  }

  .row-warning { color: #E6A23C; }

  .link {
    color: #409EFF;
    text-decoration: none;
  }

  ::v-deep .el-table .is-canceled {
    background-color: #FAFAFA;
    color: #909399;
  }
}
</style>
